// Shop data service with proper HTTP caching
// Uses ETag-based conditional requests for efficient cache validation
// Ensures immediate cache invalidation across all clients when data changes

import { Shop, OpeningHours } from '../types/Shop';
import { createCache } from '../utils/cache';

interface ApiResponse {
    success: boolean;
    data: Shop[];
    count: number;
    error?: string;
}

interface CachedData {
    data: Shop[];
    etag: string;
    timestamp: number;
}

// Create cache with ETag support and shorter TTL for validation
const shopCache = createCache<CachedData>('shops', 5); // 5 minutes TTL for validation checks

// Helper to create standard opening hours
const createStandardHours = (openTime: string, closeTime: string): OpeningHours[] => {
    const days: OpeningHours['dayOfWeek'][] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    return days.map(day => ({
        dayOfWeek: day,
        openTime: openTime,
        closeTime: closeTime,
        isClosed: false
    }));
};

const MOCK_SHOPS: Shop[] = [
    {
        id: 'mock-1',
        name: 'Vintage Heaven',
        description: 'Best vintage clothes in town. We have a wide selection of leather jackets and denim.',
        address: 'Vestergade 12, 5000 Odense C',
        coordinates: [55.3962, 10.3905],
        tags: ['Clothes', 'Vintage'],
        openingHours: createStandardHours('10:00', '17:30'),
        phone: '12345678',
        website: 'https://vintageheaven.dk',
        imageUrl: 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        createdByUserId: 'mock-user-1',
        type: 'secondhand'
    },
    {
        id: 'mock-2',
        name: 'Retro Corner',
        description: 'Furniture from the 60s and 70s. Teak, rosewood and oak.',
        address: 'Nørregade 45, 5000 Odense C',
        coordinates: [55.3995, 10.3880],
        tags: ['Furniture', 'Retro', 'Genbrug'],
        openingHours: createStandardHours('11:00', '18:00'),
        createdByUserId: 'mock-user-2',
        type: 'secondhand'
    },
    {
        id: 'mock-3',
        name: 'The Old Barn Antiquities',
        description: 'Real antiques and country style items.',
        address: 'Rugårdsvej 100, 5000 Odense C',
        coordinates: [55.3920, 10.3650],
        tags: ['Antiques', 'Furniture'],
        openingHours: createStandardHours('10:00', '16:00'),
        createdByUserId: 'mock-user-3',
        type: 'secondhand'
    },
    {
        id: 'mock-4',
        name: 'Lumby Genbrug',
        description: 'Local flea market with everything from toys to tools.',
        address: 'Slettensvej 162, 5270 Odense N',
        coordinates: [55.4350, 10.3700],
        tags: ['Genbrug', 'Toys', 'Tools'],
        openingHours: createStandardHours('13:00', '17:00'),
        createdByUserId: 'mock-user-4',
        type: 'flea_market'
    },
    {
        id: 'mock-5',
        name: 'Books & Coffee',
        description: 'Second hand books and great coffee.',
        address: 'Brandts Passage 34, 5000 Odense C',
        coordinates: [55.3955, 10.3840],
        tags: ['Books', 'Cafe'],
        openingHours: createStandardHours('09:00', '20:00'),
        createdByUserId: 'mock-user-5',
        type: 'secondhand'
    }
];

export class ShopService {
    private static readonly CACHE_KEY = 'all_shops';
    private static readonly API_ENDPOINT = '/api/shops';

    /**
     * Get shops with ETag-based caching:
     * 1. Send request with If-None-Match header (if we have cached ETag)
     * 2. If 304 Not Modified, use cached data
     * 3. If 200 OK, cache new data with new ETag
     * 4. If API fails, fallback to cached data or empty array
     */
    static async getShops(): Promise<Shop[]> {
        console.log('🏪 [ShopService] Getting shops with ETag validation...');

        // Try cache first
        const cachedData = shopCache.get(this.CACHE_KEY);

        try {
            // Always make request with ETag header if we have one
            const headers: HeadersInit = {};
            if (cachedData?.etag) {
                headers['If-None-Match'] = cachedData.etag;
                console.log('🔍 [ShopService] Sending ETag for validation:', cachedData.etag);
            } else {
                console.log('🔄 [ShopService] No cached data, fetching fresh...');
            }

            const response = await fetch(this.API_ENDPOINT, { headers });
            console.log('📡 [ShopService] Response status:', response.status);
            console.log('📡 [ShopService] Response ETag:', response.headers.get('ETag'));

            // 304 means data hasn't changed
            if (response.status === 304) {
                console.log('✅ [ShopService] Data not modified (304), using cached shops:', cachedData?.data?.length || 0);
                return cachedData?.data || [];
            }

            // Data has changed or no cache - parse the response
            console.log('🔄 [ShopService] Fetching fresh data (status:', response.status, ')');

            if (!response.ok) {
                // If API functionality is missing (e.g. 404), return MOCK data
                console.warn(`API responded with status: ${response.status}. Returning Mock Data.`);
                return MOCK_SHOPS;
            }

            const result: ApiResponse = await response.json();
            const newETag = response.headers.get('ETag') || '';

            if (result.success && result.data) {
                console.log('✅ [ShopService] API fetch successful:', result.data.length, 'shops');

                // If API returns 0 shops, append mocks for demo purposes?
                // Or just assume if it works, it works. 
                // Let's assume on localhost we might want mocks if DB is empty.
                const shops = result.data.length > 0 ? result.data : MOCK_SHOPS;

                console.log('🏷️ [ShopService] New ETag received:', newETag);

                // Cache the result with ETag
                const cacheData: CachedData = {
                    data: shops,
                    etag: newETag,
                    timestamp: Date.now()
                };
                shopCache.set(this.CACHE_KEY, cacheData);

                return shops;
            } else {
                throw new Error(result.error || 'API returned unsuccessful response');
            }
        } catch (error) {
            console.error('⚠️ [ShopService] API fetch failed:', error);

            // If we have cached data, return it as fallback
            if (cachedData?.data) {
                console.log('📦 [ShopService] API failed, returning cached data as fallback');
                return cachedData.data;
            }

            // Fallback to MOCK data instead of empty array
            console.log('📦 [ShopService] API failed/No Cache. Returning MOCK DATA.');
            return MOCK_SHOPS;
        }
    }

    /**
     * Force refresh shop data (bypasses cache completely)
     */
    static async refreshShops(): Promise<Shop[]> {
        console.log('🔄 [ShopService] Force refreshing shops...');

        // Clear cache first
        shopCache.remove(this.CACHE_KEY);

        // Fetch fresh data
        return this.getShops();
    }

    /**
     * Preload shop data in background (for performance)
     */
    static preloadShops(): void {
        // Fire and forget - don't await
        this.getShops().catch(error => {
            console.warn('⚠️ [ShopService] Preload failed:', error);
        });
    }

    /**
     * Get cache statistics for debugging
     */
    static getCacheStats() {
        const stats = shopCache.getStats();
        const cachedData = shopCache.get(this.CACHE_KEY);

        return {
            ...stats,
            hasCachedShops: !!cachedData?.data,
            cachedShopCount: cachedData?.data?.length || 0,
            etag: cachedData?.etag || null,
            cacheTimestamp: cachedData?.timestamp || null,
        };
    }

    /**
     * Invalidate cache when shops are modified
     * This forces the next getShops() call to fetch fresh data
     */
    static invalidateCache(): void {
        console.log('🗑️ [ShopService] Invalidating shop cache due to data change');
        shopCache.remove(this.CACHE_KEY);
    }

    /**
     * Clear shop cache (useful for debugging or forced refresh)
     */
    static clearCache(): void {
        console.log('🗑️ [ShopService] Clearing shop cache...');
        shopCache.clear();
    }
}

// Export convenience functions for easier usage
export const getShops = () => ShopService.getShops();
export const refreshShops = () => ShopService.refreshShops();
export const preloadShops = () => ShopService.preloadShops();

export const clearShopCache = () => ShopService.clearCache();